import { ToggledInput } from '../../../GlobalComponents/Inputs/Inputs'
import { fqAlert } from '../../../GlobalComponents/NotificationManager/NotificationManager'
import {
    CommissionOrderImport,
    CommissionOrderImportItem,
    ImportCommissionOrdersReturn,
    ImportCommissionOrdersReturnLI,
    ImportCommissionOrdersReturnProblem,
} from '../../../features/order/orderAPI'
import { formatMoney } from '../../../utils'
import { ImportPartNumberTextBox } from './ImportPartNumberTextBox'
import styles from './../ImportCommissionOrders.module.scss'

const getRowProblem = (dataItem: ImportCommissionOrdersReturnLI, fieldName: string) => {
    if (dataItem?.Problems.length > 0) {
        const fieldProblems = dataItem?.Problems.filter((p) => p.FieldName === fieldName)
        if (fieldProblems.length > 0) return fieldProblems
    }

    return []
}

const getRowValue = (
    dataItem: ImportCommissionOrdersReturnLI,
    lineItem: boolean,
    fieldName: string
) => {
    const o = lineItem ? dataItem.LineItem : dataItem
    return o[fieldName as keyof typeof dataItem]
}

export const TextCell = (
    dataItem: ImportCommissionOrdersReturnLI,
    lineItem: boolean,
    fieldName: string,
    textAlign: 'left' | 'right' | 'center',
    showProblems?: boolean,
    OnCorrectionMade?: (value: string) => void
) => {
    const problems = getRowProblem(dataItem, fieldName)
    let problem: ImportCommissionOrdersReturnProblem | undefined
    if (problems.length > 0) problem = problems[0]
    const value = getRowValue(dataItem, lineItem, fieldName)

    return (
        <td style={{ height: problem ? '55px' : 'auto' }}>
            <div style={{ textAlign: textAlign, padding: '10px 24px' }}>
                {(showProblems && problem?.Message) || !value ? (
                    <ToggledInput
                        value={value ? value.toString() : ''}
                        onChange={async (newVal: any) => {
                            OnCorrectionMade && OnCorrectionMade(newVal)
                        }}
                        textAlign={textAlign}
                    />
                ) : (
                    value
                )}
            </div>
            {showProblems && problem && (
                <div className={(problem.Severity = 1 ? styles.warningInfo : styles.errorInfo)}>
                    {problem.Message}
                </div>
            )}
        </td>
    )
}

export const MoneyCell = (
    dataItem: ImportCommissionOrdersReturnLI,
    lineItem: boolean,
    fieldName: string,
    showProblems?: boolean
) => {
    const problems = getRowProblem(dataItem, fieldName)
    let problem: ImportCommissionOrdersReturnProblem | undefined
    if (problems.length > 0) problem = problems[0]
    const value = getRowValue(dataItem, lineItem, fieldName)

    return (
        <td
            style={{
                textAlign: 'right',
                padding: '10px 24px',
                height: showProblems && problem ? '55px' : 'auto',
            }}
        >
            <div>
                {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                }).format(value)}
            </div>
            {showProblems && problem && (
                <div className={(problem.Severity = 1 ? styles.warningInfo : styles.errorInfo)}>
                    {problem.Message}
                </div>
            )}
        </td>
    )
}

export const PercentCell = (
    dataItem: ImportCommissionOrdersReturnLI,
    lineItem: boolean,
    fieldName: string,
    showProblems?: boolean
) => {
    const problems = getRowProblem(dataItem, fieldName)
    let problem: ImportCommissionOrdersReturnProblem | undefined
    if (problems.length > 0) problem = problems[0]
    const value = getRowValue(dataItem, lineItem, fieldName)

    return (
        <td
            style={{
                textAlign: 'right',
                padding: '10px 24px',
                height: showProblems && problem ? '55px' : 'auto',
            }}
        >
            <div>
                {value.toLocaleString('en-US', {
                    style: 'percent',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2,
                })}
            </div>
            {showProblems && problem && (
                <div className={(problem.Severity = 1 ? styles.warningInfo : styles.errorInfo)}>
                    {problem.Message}
                </div>
            )}
        </td>
    )
}

export const CommissionPercentageCell = (
    dataItem: ImportCommissionOrdersReturnLI,
    OnCorrectionMade: (value: string) => void,
    showProblems?: boolean
) => {
    const problems = getRowProblem(dataItem, 'CommissionPercentage')
    let problem: ImportCommissionOrdersReturnProblem | undefined
    if (problems.length > 0) problem = problems[0]

    return (
        <td style={{ padding: '10px 24px', height: showProblems && problem ? '55px' : 'auto' }}>
            <div style={{ paddingRight: '10px', textAlign: 'right' }}>
                {(showProblems && problem?.Message) ||
                (!dataItem.LineItem.CommissionPercentage &&
                    dataItem.LineItem.CommissionPercentage !== 0) ? (
                    <ToggledInput
                        value={
                            dataItem.LineItem.CommissionPercentage
                                ? dataItem.LineItem.CommissionPercentage.toString()
                                : ''
                        }
                        onChange={async (newVal: any) => {
                            OnCorrectionMade && OnCorrectionMade(newVal)
                        }}
                        textAlign='right'
                    />
                ) : (
                    (dataItem.LineItem.CommissionPercentage / 100).toLocaleString('en-US', {
                        style: 'percent',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 2,
                    })
                )}
            </div>
            {showProblems && problem && (
                <div className={(problem.Severity = 1 ? styles.warningInfo : styles.errorInfo)}>
                    {problem.Message}
                </div>
            )}
        </td>
    )
}
interface PartNumberCellProps {
    dataItem: ImportCommissionOrdersReturnLI
    customerAccountCode: string
    OnChosenCommissionOrderChange: (dataItem?: ImportCommissionOrdersReturn) => void
    chosenCommissionOrder: ImportCommissionOrdersReturn
    commissionsInput: CommissionOrderImport
    OnCorrectionMade: (dataItem: CommissionOrderImport) => void
    showProblems?: boolean
}

export const PartNumberCell = (props: PartNumberCellProps) => {
    const {
        dataItem,
        customerAccountCode,
        OnCorrectionMade,
        OnChosenCommissionOrderChange,
        chosenCommissionOrder,
        commissionsInput,
        showProblems,
    } = props

    const problems = getRowProblem(dataItem, 'PartNumber')
    let problem: ImportCommissionOrdersReturnProblem | undefined
    if (problems.length > 0) problem = problems[0]

    return (
        <td style={{ padding: '10px 24px', height: showProblems && problem ? '55px' : 'auto' }}>
            <div style={{ paddingRight: '10px' }}>
                {(showProblems && problem?.Message) || !dataItem.LineItem.PartNumber ? (
                    <ImportPartNumberTextBox
                        partNumber={
                            dataItem.LineItem.PartNumber
                                ? dataItem.LineItem.PartNumber.toString()
                                : ''
                        }
                        accountCode={customerAccountCode}
                        rowNumber={dataItem.Row}
                        OnChosenCommissionOrderChange={OnChosenCommissionOrderChange}
                        chosenCommissionOrder={chosenCommissionOrder}
                        commissionsInput={commissionsInput}
                        OnCorrectionMade={OnCorrectionMade}
                    />
                ) : (
                    <div>{dataItem.LineItem.PartNumber}</div>
                )}
            </div>
            {showProblems && problem && (
                <div className={(problem.Severity = 1 ? styles.warningInfo : styles.errorInfo)}>
                    {problem.Message}
                </div>
            )}
        </td>
    )
}

export const QuantityCell = (
    dataItem: ImportCommissionOrdersReturnLI,
    showProblems: boolean,
    OnCorrectionMade: (value: string) => void
) => {
    const problems = getRowProblem(dataItem, 'OrderQuantity')
    let problem: ImportCommissionOrdersReturnProblem | undefined
    if (problems.length > 0) problem = problems[0]

    return (
        <td style={{ padding: '10px 24px', height: showProblems && problem ? '55px' : 'auto' }}>
            <div style={{ paddingRight: '10px', textAlign: 'right' }}>
                {(showProblems && problem?.Message) || !dataItem.LineItem.OrderQuantity ? (
                    <ToggledInput
                        value={
                            dataItem.LineItem.OrderQuantity
                                ? dataItem.LineItem.OrderQuantity.toString()
                                : ''
                        }
                        onChange={async (newVal: any) => {
                            OnCorrectionMade && OnCorrectionMade(newVal)
                        }}
                        textAlign='right'
                    />
                ) : (
                    dataItem.LineItem.OrderQuantity
                )}
            </div>
            {showProblems && problem && (
                <div className={(problem.Severity = 1 ? styles.warningInfo : styles.errorInfo)}>
                    {problem.Message}
                </div>
            )}
        </td>
    )
}

export const DeleteLineCell = (
    OnLineDelete: (dataItem: any) => void,
    dataItem: ImportCommissionOrdersReturnLI,
    disabled?: boolean
) => {
    return (
        <td style={{ padding: '10px 6px' }}>
            {!disabled && (
                <div
                    onClick={() => {
                        OnLineDelete(dataItem)
                    }}
                    style={{ cursor: 'pointer' }}
                >
                    <span className='k-icon k-i-trash'></span>
                </div>
            )}
        </td>
    )
}
